import React, { useEffect, useMemo, useState } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts';
import Container from '../../../layouts/Container';
import TitleBar from '../../processes/components/TitleBar';
import Modal from '../../../components/Modal';
import ButtonForm from '../../../components/ButtonForm';
import TextInput from '../../../components/TextInput';
import SelectionForm from '../../../components/SelectionForm';
import Spinner from '../../../components/Spinner';
import { useAuth } from '../../../providers/AuthContext';
import { useAppContext } from '../../../providers/AppContext';
import {
  getStorageSummary,
  getStorageDeviceBreakdown,
  getStorageProcessBreakdown,
  sendStorageReportByEmail,
} from '../../../services/apiStorageAnalysis';

const MESES = {
  "1": "Enero", "2": "Febrero", "3": "Marzo", "4": "Abril",
  "5": "Mayo", "6": "Junio", "7": "Julio", "8": "Agosto",
  "9": "Septiembre", "10": "Octubre", "11": "Noviembre", "12": "Diciembre",
};

const formatBytes = (bytes) => {
  if (!bytes || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  return `${(bytes / Math.pow(1024, i)).toFixed(2)} ${units[i]}`;
};

const formatSeconds = (seconds) => {
  if (seconds === null || seconds === undefined) return 'Sin datos suficientes';
  if (seconds < 60) return `${seconds.toFixed(1)} seg`;
  if (seconds < 3600) return `${(seconds / 60).toFixed(1)} min`;
  if (seconds < 86400) return `${(seconds / 3600).toFixed(1)} hs`;
  return `${(seconds / 86400).toFixed(1)} días`;
};

// Tarjeta de resumen (KPI), reutilizada en las dos secciones del módulo.
const StatCard = ({ label, value, accent = "#10174c" }) => (
  <div className="flex-1 min-w-[160px] bg-white border border-gray-200 rounded p-4 shadow-sm">
    <div className="text-xs text-gray-500 font-semibold uppercase">{label}</div>
    <div className="text-2xl font-bold mt-1" style={{ color: accent }}>{value}</div>
  </div>
);

const CustomBarTooltip = ({ active, payload, label }) => {
  if (active && payload?.length) {
    return (
      <div className="bg-black text-white px-2 py-1 rounded opacity-90 text-sm">
        <div>{label}</div>
        <div className="font-semibold">{formatBytes(payload[0].value)}</div>
      </div>
    );
  }
  return null;
};

function StorageAnalysis() {
  const { auth, hasPermission, userEmail } = useAuth();
  const { addToast } = useAppContext();

  const currentDate = new Date();
  const [month, setMonth] = useState(String(currentDate.getMonth() + 1));
  const [year, setYear] = useState(String(currentDate.getFullYear()));

  const yearOptions = useMemo(() => {
    const currentYear = currentDate.getFullYear();
    const years = {};
    for (let y = currentYear; y >= currentYear - 4; y--) {
      years[String(y)] = String(y);
    }
    return years;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [loadingSummary, setLoadingSummary] = useState(true);
  const [loadingBreakdown, setLoadingBreakdown] = useState(true);
  const [telemetrySummary, setTelemetrySummary] = useState(null);
  const [processSummary, setProcessSummary] = useState(null);
  const [deviceBreakdown, setDeviceBreakdown] = useState(null);
  const [processBreakdown, setProcessBreakdown] = useState(null);

  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailTo, setEmailTo] = useState(userEmail || "");
  const [sendingReport, setSendingReport] = useState(false);

  const canSendReport = hasPermission('StorageAnalysis', 'Report', 'READ');

  const refreshSummary = async () => {
    try {
      setLoadingSummary(true);
      const data = await getStorageSummary(auth?.token, month, year);
      setTelemetrySummary(data?.telemetrySummary || null);
      setProcessSummary(data?.processSummary || null);
      setLoadingSummary(false);
    } catch (error) {
      addToast("No se pudo obtener el resumen de almacenamiento", 2);
      setLoadingSummary(false);
    }
  };

  const refreshBreakdown = async () => {
    try {
      setLoadingBreakdown(true);
      const [devices, processes] = await Promise.all([
        getStorageDeviceBreakdown(auth?.token, month, year),
        getStorageProcessBreakdown(auth?.token, month, year),
      ]);
      setDeviceBreakdown(devices);
      setProcessBreakdown(processes);
      setLoadingBreakdown(false);
    } catch (error) {
      addToast("No se pudo obtener el detalle de almacenamiento", 2);
      setLoadingBreakdown(false);
    }
  };

  useEffect(() => {
    refreshSummary();
    refreshBreakdown();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month, year]);

  const deviceChartData = useMemo(() => {
    if (!deviceBreakdown?.devices) return [];
    return [...deviceBreakdown.devices]
      .sort((a, b) => b.estimatedBytes - a.estimatedBytes)
      .slice(0, 10)
      .map((d) => ({ name: d.name, bytes: d.estimatedBytes }));
  }, [deviceBreakdown]);

  const processChartData = useMemo(() => {
    if (!processBreakdown?.processes) return [];
    return [...processBreakdown.processes]
      .sort((a, b) => b.estimatedBytes - a.estimatedBytes)
      .slice(0, 10)
      .map((p) => ({ name: p.name, bytes: p.estimatedBytes }));
  }, [processBreakdown]);

  const handleSendReport = async () => {
    if (!emailTo) {
      addToast("Ingresa un correo de destino", 2);
      return;
    }
    try {
      setSendingReport(true);
      await sendStorageReportByEmail(emailTo, auth?.token, month, year);
      addToast("Reporte enviado correctamente", 0);
      setSendingReport(false);
      setShowEmailModal(false);
    } catch (error) {
      addToast("No se pudo enviar el reporte", 2);
      setSendingReport(false);
    }
  };

  return (
    <Container>
      <div className='flex flex-col w-full h-screen'>
        <div className='min-h-10'>
          <TitleBar text={"Almacenamiento y Procesamiento"} />
        </div>
        <div className='w-full h-full overflow-y-scroll bg-graysens'>
          <div className="w-11/12 mx-auto p-4 my-2">
            <div className="flex items-center justify-between my-2 flex-wrap gap-2">
              <div className="bg-white border border-gray-200 rounded p-3 shadow-sm flex items-end gap-3">
                <div className="w-40">
                  <SelectionForm
                    text="Mes"
                    options={Object.keys(MESES)}
                    mask={MESES}
                    value={month}
                    onChange={(option) => setMonth(option)}
                  />
                </div>
                <div className="w-32">
                  <SelectionForm
                    text="Año"
                    options={Object.keys(yearOptions)}
                    mask={yearOptions}
                    value={year}
                    onChange={(option) => setYear(option)}
                  />
                </div>
              </div>
              {canSendReport && (
                <div className="w-52">
                  <ButtonForm text="Enviar reporte por correo" enabled={true} action={() => setShowEmailModal(true)} />
                </div>
              )}
            </div>

            {/* ===== Resumen ===== */}
            <h2 className="text-lg font-semibold text-gray-700 mt-2 mb-2">Resumen</h2>
            {loadingSummary ? (
              <div className='w-full flex justify-center py-8'><Spinner /></div>
            ) : (
              <>
                <div className="text-sm text-gray-500 font-semibold mt-2 mb-1">Telemetría</div>
                <div className="flex flex-wrap gap-3">
                  <StatCard label="Dispositivos" value={telemetrySummary?.deviceCount ?? 0} />
                  <StatCard label="Variables" value={telemetrySummary?.variableCount ?? 0} />
                  <StatCard label="Mediciones" value={telemetrySummary?.measureCount ?? 0} />
                  <StatCard label="Peso estimado" value={formatBytes(telemetrySummary?.estimatedBytes)} accent="#0ea5e9" />
                </div>

                <div className="text-sm text-gray-500 font-semibold mt-4 mb-1">Procesos</div>
                <div className="flex flex-wrap gap-3">
                  <StatCard label="Procesos" value={processSummary?.processCount ?? 0} />
                  <StatCard label="Registros" value={processSummary?.registerCount ?? 0} />
                  <StatCard label="Peso estimado" value={formatBytes(processSummary?.estimatedBytes)} accent="#0ea5e9" />
                </div>
              </>
            )}

            {/* ===== Reporte ===== */}
            <h2 className="text-lg font-semibold text-gray-700 mt-8 mb-2">Reporte</h2>
            {loadingBreakdown ? (
              <div className='w-full flex justify-center py-8'><Spinner /></div>
            ) : (
              <>
                <div className="flex flex-wrap gap-4">
                  <div className="flex-1 min-w-[300px] bg-white border border-gray-200 rounded p-4 shadow-sm">
                    <div className="font-semibold text-gray-700 mb-2">Peso por proceso (top 10)</div>
                    {processChartData.length === 0 ? (
                      <div className="text-sm text-gray-500 py-6 text-center">No hay procesos con datos</div>
                    ) : (
                      <ResponsiveContainer width="100%" height={260}>
                        <BarChart data={processChartData} margin={{ top: 10, right: 10, bottom: 40, left: 10 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                          <XAxis dataKey="name" stroke="#6b7280" angle={-30} textAnchor="end" interval={0} height={60} />
                          <YAxis stroke="#6b7280" tickFormatter={(v) => formatBytes(v)} width={70} />
                          <Tooltip content={<CustomBarTooltip />} />
                          <Bar dataKey="bytes" fill="#10174c" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                  </div>
                  <div className="flex-1 min-w-[300px] bg-white border border-gray-200 rounded p-4 shadow-sm">
                    <div className="font-semibold text-gray-700 mb-2">Peso por dispositivo (top 10)</div>
                    {deviceChartData.length === 0 ? (
                      <div className="text-sm text-gray-500 py-6 text-center">No hay dispositivos con datos</div>
                    ) : (
                      <ResponsiveContainer width="100%" height={260}>
                        <BarChart data={deviceChartData} margin={{ top: 10, right: 10, bottom: 40, left: 10 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                          <XAxis dataKey="name" stroke="#6b7280" angle={-30} textAnchor="end" interval={0} height={60} />
                          <YAxis stroke="#6b7280" tickFormatter={(v) => formatBytes(v)} width={70} />
                          <Tooltip content={<CustomBarTooltip />} />
                          <Bar dataKey="bytes" fill="#0ea5e9" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                  </div>
                </div>

                <div className="mt-4 bg-white border border-gray-200 rounded p-4 shadow-sm">
                  <div className="flex items-center justify-between mb-2">
                    <div className="font-semibold text-gray-700">Detalle por proceso</div>
                    <div className="text-xs text-gray-500">
                      Transmisión promedio general: <span className="font-semibold">{formatSeconds(processBreakdown?.overallAvgTransmissionSeconds)}</span>
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="text-left text-gray-500 border-b border-gray-200">
                          <th className="py-2 pr-4">Proceso</th>
                          <th className="py-2 pr-4">Parámetros</th>
                          <th className="py-2 pr-4">Registros</th>
                          <th className="py-2 pr-4">Peso estimado</th>
                          <th className="py-2 pr-4">Peso promedio por registro</th>
                          <th className="py-2 pr-4">Peso mínimo de un registro</th>
                          <th className="py-2 pr-4">Transmisión promedio</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(processBreakdown?.processes || []).map((p) => (
                          <tr key={p.processId} className="border-b border-gray-100">
                            <td className="py-2 pr-4 font-medium text-gray-700">{p.name}</td>
                            <td className="py-2 pr-4">{p.parameterCount}</td>
                            <td className="py-2 pr-4">{p.registerCount}</td>
                            <td className="py-2 pr-4">{formatBytes(p.estimatedBytes)}</td>
                            <td className="py-2 pr-4">{p.avgDocSize != null ? formatBytes(p.avgDocSize) : '--'}</td>
                            <td className="py-2 pr-4">{p.minDocSize != null ? formatBytes(p.minDocSize) : '--'}</td>
                            <td className="py-2 pr-4">{formatSeconds(p.avgTransmissionSeconds)}</td>
                          </tr>
                        ))}
                        {(processBreakdown?.processes || []).length === 0 && (
                          <tr><td className="py-4 text-gray-500" colSpan={7}>No hay procesos configurados</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="mt-4 bg-white border border-gray-200 rounded p-4 shadow-sm">
                  <div className="flex items-center justify-between mb-2">
                    <div className="font-semibold text-gray-700">Detalle por dispositivo</div>
                    <div className="text-xs text-gray-500">
                      Transmisión promedio general: <span className="font-semibold">{formatSeconds(deviceBreakdown?.overallAvgTransmissionSeconds)}</span>
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="text-left text-gray-500 border-b border-gray-200">
                          <th className="py-2 pr-4">Dispositivo</th>
                          <th className="py-2 pr-4">Variables</th>
                          <th className="py-2 pr-4">Mediciones</th>
                          <th className="py-2 pr-4">Peso estimado</th>
                          <th className="py-2 pr-4">Peso promedio por medición</th>
                          <th className="py-2 pr-4">Peso mínimo de una medición</th>
                          <th className="py-2 pr-4">Transmisión promedio</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(deviceBreakdown?.devices || []).map((d) => (
                          <tr key={d.deviceId} className="border-b border-gray-100">
                            <td className="py-2 pr-4 font-medium text-gray-700">{d.name}</td>
                            <td className="py-2 pr-4">{d.variableCount}</td>
                            <td className="py-2 pr-4">{d.measureCount}</td>
                            <td className="py-2 pr-4">{formatBytes(d.estimatedBytes)}</td>
                            <td className="py-2 pr-4">{d.avgDocSize != null ? formatBytes(d.avgDocSize) : '--'}</td>
                            <td className="py-2 pr-4">{d.minDocSize != null ? formatBytes(d.minDocSize) : '--'}</td>
                            <td className="py-2 pr-4">{formatSeconds(d.avgTransmissionSeconds)}</td>
                          </tr>
                        ))}
                        {(deviceBreakdown?.devices || []).length === 0 && (
                          <tr><td className="py-4 text-gray-500" colSpan={7}>No hay dispositivos configurados</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <Modal
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        title="Enviar reporte por correo"
        customType={2}
        colorTitle="bg-bluesens"
        size="w-1/3"
      >
        <div className="flex flex-col gap-3 p-2">
          <TextInput
            label="Correo de destino"
            id="storage_report_email"
            value={emailTo}
            onChange={(e) => setEmailTo(e.target.value)}
          />
          <ButtonForm
            text={sendingReport ? "Enviando..." : "Enviar"}
            enabled={!sendingReport}
            action={handleSendReport}
          />
        </div>
      </Modal>
    </Container>
  );
}

export default StorageAnalysis;
